/**
 * @file: useTaskProductManagement.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import {
  TaskContextProps,
  useTaskContext,
} from '@euler/app/flows/task/functions/TaskContext';
import { useRemoveProductManifestItem } from '@euler/app/flows/task/functions/products/useRemoveProductManifestItem';
import { useTaskNavigation } from '@euler/app/flows/task/functions/useTaskNavigation';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useHasProducts } from '@euler/functions/product/useHasProducts';
import { IdType } from '@euler/model/common';
import { CommonTaskStatus, InspectionTaskStatus } from '@euler/model/enum';
import { ProductWithContractId } from '@euler/model/product';
import {
  SaveTaskProductManifestOriginInfo,
  TaskProductInfo,
  TaskProductManifestItem,
} from '@euler/model/task-detail';
import { useCallback } from 'react';
import invariant from 'tiny-invariant';

export const useTaskProductManagement = (props: {
  origin: SaveTaskProductManifestOriginInfo;
}) => {
  const taskContext = useTaskContext() as TaskContextProps | undefined;
  const navigation = useTaskNavigation();
  const [hasProducts] = useHasProducts();
  const available = taskContext != null;

  const productManifestManager =
    taskContext?.taskManager.productManifestManager;

  const loading = useAppLoading();
  const removeProductManifestItem = useRemoveProductManifestItem(props);

  const canAssociateProduct =
    taskContext != null &&
    hasProducts &&
    taskContext.detail.basicInfo.status === InspectionTaskStatus.InProgress &&
    (taskContext.detail.construction.status === CommonTaskStatus.InProgress ||
      taskContext.detail.construction.status === CommonTaskStatus.Pending);

  const onViewProductDetail = useCallback(
    (item: TaskProductManifestItem) => {
      if (!available) return;
      invariant(productManifestManager != null);
      navigation.navigate('_products', {
        items$: productManifestManager!.manifest$,
        screen: '_productDetail',
        params: {
          id: item.product.id,
        },
      });
    },
    [available, navigation, productManifestManager],
  );

  const onAddProduct = useCallback(
    (
      subject: string,
      options?: {
        stage?: boolean;
        callback?: (props: {
          hasChanges: boolean;
          products: TaskProductInfo[];
        }) => void;
      },
    ) => {
      if (!available) return;
      invariant(productManifestManager != null);
      navigation.navigate('_products', {
        items$: productManifestManager.manifest$,
        screen: '_addProduct',
        params: {
          subject,
          onGetManifest$: () => productManifestManager.manifest$,
          onDone: async (selection: ProductWithContractId[]) => {
            const products = selection.map<TaskProductInfo>(x => ({
              id: x.id,
              name: x.name,
              coverUrl: x.coverUrl,
              title: x.title ?? undefined,
              brandId: x.brandId,
              brandName: x.brandName,
              agentId: x.agentId,
              agentName: x.agentName,
              sourceContractId: x.sourceContractId,
            }));
            let changes = 0;
            for (const product of products) {
              const res = productManifestManager.addProductForSubject(
                subject,
                product,
                props.origin.type,
              );
              changes += res ? 1 : 0;
            }
            if (changes > 0) {
              if (!options?.stage) {
                await loading.showWithAction(async () => {
                  await productManifestManager.save({
                    origin: props.origin,
                  });
                  navigation.goBack();
                });
              }
            }
            options?.callback?.({
              hasChanges: changes > 0,
              products,
            });
          },
        },
      });
    },
    [available, loading, navigation, productManifestManager, props.origin],
  );

  const onRemoveProduct = useCallback(
    (subject: string, productId: IdType, options?: { stage?: boolean }) => {
      if (!available) return;
      invariant(productManifestManager != null);
      void removeProductManifestItem(
        productManifestManager,
        subject,
        productId,
        options,
      );
    },
    [available, productManifestManager, removeProductManifestItem],
  );

  return {
    available,
    hasProducts,
    canAssociateProduct,
    onAddProduct,
    onViewProductDetail,
    onRemoveProduct,
  };
};
